import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { generalChatSettingsService } from '../services';
import type { GeneralChatSettings } from '../types';

interface GeneralChatSettingsState {
  // Settings data
  settings: GeneralChatSettings;
  originalSettings: GeneralChatSettings | null;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;

  // Error state
  error: string | null;

  // Form state
  hasUnsavedChanges: boolean;

  // Actions
  loadSettings: () => Promise<void>;
  updateTemperature: (temperature: number) => void;
  updateSystemInstructions: (instructions: string) => void;
  saveSettings: () => Promise<boolean>;
  resetToOriginal: () => void;
  clearError: () => void;
}

const defaultSettings: GeneralChatSettings = {
  temperature: 0.7,
  systemInstructions: 'You are a helpful assistant',
  kValue: 5,
};

export const useGeneralChatSettingsStore = create<GeneralChatSettingsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      settings: defaultSettings,
      originalSettings: null,
      isLoading: false,
      isSaving: false,
      error: null,
      hasUnsavedChanges: false,

      // Actions
      loadSettings: async () => {
        set(
          { isLoading: true, error: null },
          false,
          'generalChatSettings/loadSettings:start'
        );

        try {
          const settings = await generalChatSettingsService.getSettings();
          set(
            {
              settings,
              originalSettings: { ...settings },
              isLoading: false,
              hasUnsavedChanges: false,
            },
            false,
            'generalChatSettings/loadSettings:success'
          );
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to load settings';
          set(
            {
              error: errorMessage,
              isLoading: false,
            },
            false,
            'generalChatSettings/loadSettings:error'
          );
        }
      },

      updateTemperature: (temperature: number) => {
        const { originalSettings } = get();
        const newSettings = { ...get().settings, temperature };
        const hasChanges = originalSettings
          ? JSON.stringify(newSettings) !== JSON.stringify(originalSettings)
          : true;

        set(
          {
            settings: newSettings,
            hasUnsavedChanges: hasChanges,
            error: null,
          },
          false,
          'generalChatSettings/updateTemperature'
        );
      },

      updateSystemInstructions: (instructions: string) => {
        const { originalSettings } = get();
        const newSettings = {
          ...get().settings,
          systemInstructions: instructions,
        };
        const hasChanges = originalSettings
          ? JSON.stringify(newSettings) !== JSON.stringify(originalSettings)
          : true;

        set(
          {
            settings: newSettings,
            hasUnsavedChanges: hasChanges,
            error: null,
          },
          false,
          'generalChatSettings/updateSystemInstructions'
        );
      },

      saveSettings: async () => {
        const { settings } = get();
        set(
          { isSaving: true, error: null },
          false,
          'generalChatSettings/saveSettings:start'
        );

        try {
          await generalChatSettingsService.updateSettings(settings);
          set(
            {
              originalSettings: { ...settings },
              isSaving: false,
              hasUnsavedChanges: false,
            },
            false,
            'generalChatSettings/saveSettings:success'
          );
          return true;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to save settings';
          set(
            {
              error: errorMessage,
              isSaving: false,
            },
            false,
            'generalChatSettings/saveSettings:error'
          );
          return false;
        }
      },

      resetToOriginal: () => {
        const { originalSettings } = get();
        if (originalSettings) {
          set(
            {
              settings: { ...originalSettings },
              hasUnsavedChanges: false,
              error: null,
            },
            false,
            'generalChatSettings/resetToOriginal'
          );
        }
      },

      clearError: () => {
        set({ error: null }, false, 'generalChatSettings/clearError');
      },
    }),
    {
      name: 'general-chat-settings-store',
    }
  )
);
